import React, { useEffect, useId, useRef, useState, Suspense } from 'react';
import classNames from 'classnames';
import { useSidebar } from './SidebarContext';
import { sidebarRegistry } from './SidebarRegistry';
import { SidebarPortal } from './SidebarPortal';
import { Spinner, Center } from '@chakra-ui/react';
import {
  SIDEBAR_EXPANDED_WIDTH_PX,
  SIDEBAR_RAIL_WIDTH_PX,
  SIDEBAR_WIDTH_PX,
} from './sidebarLayout.constants';
import { resolveWorkspaceMode } from '../../Pages/Main/Ai/workspace/workspaceMode';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import './sidebar.scss';

const PanelFallback: React.FC = () => (
  <Center h="100%">
    <Spinner size="xl" color="blue.500" thickness="4px" />
  </Center>
);

/**
 * Hosts one registered panel's `Content` (+ optional `Rail`). Keyed by
 * `activeId` in the parent so switching panels always remounts this
 * component — that's what makes it safe to call a *different*
 * `registration.useSharedState` hook at this stable call site: each
 * `activeId` gets brand-new hook slots instead of reusing another panel's.
 */
const PanelShell: React.FC<{
  registration: ReturnType<typeof sidebarRegistry.getRegistration>;
  panelProps: any;
  showRail: boolean;
}> = ({ registration, panelProps, showRail }) => {
  const sharedState = registration?.useSharedState?.(panelProps);
  const mergedProps =
    sharedState !== undefined ? { ...panelProps, sharedState } : panelProps;
  const Content = registration?.Content;
  const Rail = registration?.Rail;

  if (!Content) return null;

  return (
    <div className="sidebar-panel__body">
      {showRail && Rail && (
        <aside className="sidebar-panel__rail">
          <Suspense fallback={<PanelFallback />}>
            <Rail {...mergedProps} />
          </Suspense>
        </aside>
      )}
      <div className="sidebar-panel__content-wrapper">
        <div className="sidebar-panel__content">
          <Suspense fallback={<PanelFallback />}>
            <Content {...mergedProps} />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export const SidebarContainer: React.FC = () => {
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    activeId,
    props,
    close,
    displayMode,
    setDisplayMode,
    isExpanded,
    toggleExpanded,
  } = useSidebar();
  const [shouldRender, setShouldRender] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const isDocked = displayMode === 'docked';
  const registration = activeId ? sidebarRegistry.getRegistration(activeId) : undefined;

  const mode = resolveWorkspaceMode(displayMode, isExpanded);
  // Rail is visible in expanded docked sidebar AND in fullscreen floating modal
  // (matches the Cloudflare Sphere layout where the left column stays in the modal).
  const showRail = (mode === 'expanded-sidebar' || mode === 'fullscreen') && !!registration?.Rail;
  const isFullPage = mode === 'fullscreen';
  const isModal = mode === 'overlay' || mode === 'fullscreen';

  // Activate focus trap only in modal (overlay/fullscreen) modes — docked sidebar
  // is not modal and must not trap focus away from the underlying page.
  useFocusTrap(panelRef, isOpen && isModal);

  // Sync animation/render states to allow exit transitions
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (isOpen) {
      setShouldRender(true);
      // Let the browser paint the initial closed state before adding active classes
      timeoutId = setTimeout(() => {
        setIsTransitioning(true);
      }, 20);
    } else {
      setIsTransitioning(false);
      // Wait for exit transition to finish before unmounting (matches SCSS 300ms transition)
      timeoutId = setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  // Handle Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const widthPx =
    isDocked && isExpanded ? SIDEBAR_EXPANDED_WIDTH_PX : SIDEBAR_WIDTH_PX;

  useEffect(() => {
    const main = document.getElementById('main');
    const isDockedOpen = isOpen && isDocked;

    main?.classList.toggle('dashboard--sidebar-docked', isDockedOpen);
    if (isDockedOpen) {
      main?.style.setProperty('--right-sidebar-width', `${widthPx}px`);
    }

    return () => {
      main?.classList.remove('dashboard--sidebar-docked');
      main?.style.removeProperty('--right-sidebar-width');
    };
  }, [isOpen, isDocked, widthPx]);

  if (!shouldRender || !activeId) return null;

  if (!registration?.Content) return null;

  const canExpandDocked = isDocked && !!registration.Rail;
  const hideControls = !!registration.hideContainerControls;

  return (
    <SidebarPortal>
      {!isDocked && (
        <div
          className={classNames('sidebar-backdrop', {
            'sidebar-backdrop--active': isTransitioning,
          })}
          onClick={close}
          aria-hidden="true"
        />
      )}
      <div
        id={panelId}
        ref={panelRef}
        className={classNames('sidebar-panel', {
          'sidebar-panel--active': isTransitioning,
          'sidebar-panel--docked': isDocked,
          'sidebar-panel--expanded': showRail,
          'sidebar-panel--full-page': isFullPage,
        })}
        style={
          {
            '--sidebar-width': `${widthPx}px`,
            '--sidebar-rail-width': `${SIDEBAR_RAIL_WIDTH_PX}px`,
          } as React.CSSProperties
        }
        role="dialog"
        aria-modal={isModal}
        aria-label={activeId}
      >
        {!hideControls && (
          <>
            {/* Dock / undock toggle */}
            <button
              type="button"
              className="sidebar-panel__display-mode-btn"
              onClick={() => setDisplayMode(isDocked ? 'overlay' : 'docked')}
              aria-label={isDocked ? 'Open sidebar as overlay' : 'Dock sidebar'}
              title={isDocked ? 'Open as overlay' : 'Dock sidebar'}
              aria-controls={panelId}
              aria-expanded={isOpen}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {isDocked ? (
                  <>
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M15 3v18" />
                  </>
                ) : (
                  <>
                    <path d="M12 17v5" />
                    <path d="M9 4V2" />
                    <path d="M15 4V2" />
                    <path d="M18 8V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v2" />
                    <path d="M6 8h12l-2 6H8L6 8Z" />
                  </>
                )}
              </svg>
            </button>

            {/* Expand / collapse — only available in docked mode with a Rail */}
            {canExpandDocked && (
              <button
                type="button"
                className="sidebar-panel__expand-btn"
                onClick={toggleExpanded}
                aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                title={isExpanded ? 'Collapse' : 'Expand'}
                aria-controls={panelId}
                aria-expanded={isExpanded}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {isExpanded ? (
                    <>
                      <path d="M15 3h6v6" />
                      <path d="M9 21H3v-6" />
                      <path d="M21 3l-7 7" />
                      <path d="M3 21l7-7" />
                    </>
                  ) : (
                    <>
                      <path d="M9 3H3v6" />
                      <path d="M15 21h6v-6" />
                      <path d="M3 3l7 7" />
                      <path d="M21 21l-7-7" />
                    </>
                  )}
                </svg>
              </button>
            )}

            {/* Full-page expand/collapse — only visible in overlay/fullscreen modes */}
            {!isDocked && (
              <button
                data-kumo-component="Button"
                className="sidebar-panel__full-page-btn group flex shrink-0 font-medium select-none border-0 focus:outline-none focus:ring-kumo-focus/50 focus-visible:ring-2 focus-visible:ring-kumo-brand cursor-pointer disabled:cursor-not-allowed disabled:text-kumo-subtle gap-1 rounded-md text-xs items-center justify-center p-0 size-6.5 text-kumo-default hover:bg-kumo-tint shadow-none bg-inherit !h-7 !w-7"
                type="button"
                onClick={toggleExpanded}
                aria-label={isFullPage ? 'Exit fullscreen' : 'Expand to fullscreen'}
                title={isFullPage ? 'Exit fullscreen' : 'Expand to fullscreen'}
                aria-controls={panelId}
                aria-expanded={isFullPage}
              >
                <span className="contents">
                  <i
                    className={classNames(
                      'icon',
                      isFullPage ? 'icon-collapse-sidebar' : 'icon-expand-sidebar',
                      'relative',
                    )}
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}

            {/* Close */}
            <button
              type="button"
              className="sidebar-panel__close-btn"
              onClick={close}
              aria-label="Close sidebar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </>
        )}

        <PanelShell
          registration={registration}
          panelProps={props}
          showRail={showRail}
        />
      </div>
    </SidebarPortal>
  );
};
